import * as Styled from "./EventDetailsModal.styles";

import { Accordion, Contact } from "../../components";
import {
  AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon,
  CategoryOutlined as CategoryOutlinedIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  Edit as EditIcon,
  LoginOutlined as LoginOutlinedIcon,
  Microsoft as MicrosoftIcon,
  RemoveCircleOutlineOutlined as RemoveCircleOutlineOutlinedIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  SIGNUP_USER_TO_EVENT,
  UNSIGNUP_USER_TO_EVENT,
} from "../../services/endpoints";
import {
  dateToUnix,
  getAmOrPm,
  getHourFromMilliseconds,
  getMinutesFromMilliseconds,
  getMonthAndDayFromMilliseconds,
  nthNumber,
  unixDateDiff,
} from "../../utils/utils";
import {
  getEvent,
  getEventSignups,
  refreshEvent,
  refreshEventSignups,
} from "../../store/actions/eventActions";
import { memo, useEffect, useState } from "react";
import BaseModal from "../BaseModal/BaseModal";
import DOMPurify from "dompurify";
import { Event } from "../../types/event";
import EventSignUpsModal from "../EventSignUpsModal/EventSignUpsModal";
import EventFeedbackResultsModal from "../EventFeedbackResultsModal/EventFeedbackResultsModal";
import { GatherDefaultImage } from "../../assets";
import { RootState } from "../../store/store";
import ShareEventModal from "../ShareEventModal/ShareEventModal";
import SignUpSurveyModal from "../SignUpSurveyModal/SignUpSurveyModal";
import { SurveyAnswer } from "../../types/surveyquestions";
import axiosAPI from "../../services/axios";
import { createSelector } from "@reduxjs/toolkit";
import { differenceInMinutes } from "date-fns";
import { getAllUserEvents } from "../../store/actions/userEventsActions";
import { useAppDispatch } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

type EventDetailsModalProps = {
  open: any;
  onClose: any;
  eventId: string;
};

const EventDetailsModal = memo(
  ({ open, onClose, eventId }: EventDetailsModalProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [signupUserLoading, setSignupUserLoading] = useState(false);
    const [unsignupUserLoading, setUnsignupUserLoading] = useState(false);
    const [showSignUpSurveyModal, setShowSignUpSurveyModal] = useState(false);
    const [showEventSignUpsModal, setShowEventSignupsModal] = useState(false);
    const [showEventFeedbackResultsModal, setShowEventFeedbackResultsModal] =
      useState(false);
    const [showQrCodeDetailsModal, setShowQrCodeDetailsModal] = useState(false);

    const selectUser = (state: RootState) => state.user;
    const selectSignupsLoading = (state: RootState) =>
      state.event.signups.loading;
    const selectSignups = (state: RootState) => state.event.signups.data;
    const selectEventLoading = (state: RootState) => state.event.loading;
    const selectEvent = (state: RootState) => state.event.data;

    const selectUserAndEvents = createSelector(
      [
        selectUser,
        selectSignupsLoading,
        selectSignups,
        selectEventLoading,
        selectEvent,
      ],
      (user, signupsLoading, signups, eventLoading, event) => {
        return { user, signupsLoading, signups, eventLoading, event };
      }
    );

    const metrics = useSelector(
      (state: RootState) => state.eventFeedbacks.data
    );
    const metricsLoading = useSelector(
      (state: RootState) => state.eventFeedbacks.loading
    );

    const { user, signups, signupsLoading, eventLoading, event } =
      useSelector(selectUserAndEvents);

    const currentTime = new Date();
    const isPastEndTime = event?.endDateTime
      ? currentTime > new Date(event.endDateTime * 1000)
      : false;
    const isCancelled = event?.status === "CANCELLED";

    const isSignedUp = signups?.find(
      (signup) => signup.participantEmail === user.email
    );

    const signupUserToEvent = async (surveyAnswers?: SurveyAnswer[]) => {
      if (event) {
        setSignupUserLoading(true);
        const signupUserObj = {
          participantEmail: user.email,
          participantName: user.name,
          participantTitle: user.jobTitle,
          ...(surveyAnswers && { signUpSurveySubmission: surveyAnswers }),
        };
        await axiosAPI
          .post(SIGNUP_USER_TO_EVENT(event.eventId), signupUserObj)
          .then((response: any) => {
            setSignupUserLoading(false);
            dispatch(getEventSignups(eventId));
            dispatch(getAllUserEvents(user.email));
          })
          .catch((error) => {
            setSignupUserLoading(false);
          });
      }
    };

    const unsignUserToEvent = async () => {
      if (event) {
        setUnsignupUserLoading(true);
        const unsignupUserObj = {
          participantEmail: user.email,
        };
        await axiosAPI
          .post(UNSIGNUP_USER_TO_EVENT(event.eventId), unsignupUserObj)
          .then((response: any) => {
            setUnsignupUserLoading(false);
            dispatch(getEventSignups(eventId));
            dispatch(getAllUserEvents(user.email));
          })
          .catch((error) => {
            setUnsignupUserLoading(false);
          });
      }
    };

    const generateEventLink = () => {
      const url = new URL(window.location.href);
      const baseLink = `${url.protocol}//${url.host}`;
      return `${baseLink}/events?eventId=${eventId}`;
    };

    useEffect(() => {
      let intervalId: NodeJS.Timeout;

      if (eventId !== "" && open) {
        if (event && event.eventId === eventId) {
          dispatch(refreshEvent(eventId));
          dispatch(refreshEventSignups(eventId));
        } else {
          dispatch(getEvent(eventId));
          dispatch(getEventSignups(eventId));
        }

        intervalId = setInterval(() => {
          if (eventId !== "") {
            dispatch(refreshEvent(eventId));
            dispatch(refreshEventSignups(eventId));
          }
        }, 30000);
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, open]);

    const officeLocationMapping: { [key: string]: string } = {
      Boston: "399 Boylston St #1000, Boston, MA 02116",
      Hartford: "185 Asylum St #3201, Hartford, CT 06103",
      "Boston & Hartford (New England)":
        "399 Boylston St #1000, Boston, MA 02116 (Boston Office) & 185 Asylum St #3201, Hartford, CT 06103 (Hartford Office)",
    };

    const generateEventLocationMap = (event: Event) => {
      if (event.format !== "Virtual") {
        let location = event.location;

        if (event.eventType === "On-site") {
          if (event.rooms.length > 0) {
            location = officeLocationMapping[event.office] || event.location;
          }
        }

        if (location) {
          return location
            .replace(/\(.*?\)/g, "")
            .split("&")
            .map((loc, index) => (
              <Styled.MapContainer
                key={index}
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/place?key=${
                  process.env.REACT_APP_GOOGLE_API_KEY || ""
                }&q=${encodeURIComponent(loc.trim())}`}
              ></Styled.MapContainer>
            ));
        }
      }
      return null;
    };

    const generateLiveEventButton = (event: Event, user: any) => {
      const currentTime = new Date();
      const startTime = new Date(event.startDateTime * 1000);
      const endTime = new Date(event.endDateTime * 1000);

      const isEventCreator = user.email === event.eventOrganizerEmail;
      const isWithin15MinutesOfStart =
        Math.abs(differenceInMinutes(currentTime, startTime)) <= 15;

      const isPastEndTime = currentTime > endTime;

      const isDisabled =
        !isEventCreator &&
        !(
          isWithin15MinutesOfStart ||
          event.status === "ACTIVE" ||
          isPastEndTime
        );

      return (
        <Button
          startIcon={isPastEndTime ? <VisibilityIcon /> : <LoginOutlinedIcon />}
          variant="contained"
          onClick={() => {
            navigate(`/live/${event.eventId}`);
          }}
          disabled={isDisabled}
        >
          {isPastEndTime ? "View Completed Event" : "Join Live Event"}
        </Button>
      );
    };

    const theme = useTheme();
    return (
      <BaseModal
        open={open}
        onClose={onClose}
        tabletWidth="80%"
        desktopWidth="940px"
        height="95vh"
        fullScreenMobile
      >
        {eventLoading || !event ? (
          <Stack
            direction="column"
            width="100%"
            height="100%"
            style={{
              marginTop: theme.spacing(3),
            }}
          >
            <Styled.ScrollableContent
              style={{
                overflow: "hidden",
              }}
            >
              <Styled.SkeletonHero variant="rectangular" />
              <Stack
                direction={{ mobile: "column", desktop: "row" }}
                width="100%"
                height="100%"
                spacing={{ mobile: 2, desktop: 2 }}
              >
                <Styled.SkeletonContent variant="rectangular" />
                <Styled.SkeletonContent variant="rectangular" />
              </Stack>
            </Styled.ScrollableContent>
            <Styled.ButtonsContainer>
              <Styled.SkeletonButton variant="rectangular" />
            </Styled.ButtonsContainer>
          </Stack>
        ) : (
          <Stack
            direction="column"
            width="100%"
            style={{ height: "100%", marginTop: theme.spacing(3) }}
          >
            <ShareEventModal
              link={generateEventLink()}
              open={showQrCodeDetailsModal}
              onClose={() => {
                setShowQrCodeDetailsModal(false);
              }}
            />
            {event.signUpSurvey && (
              <SignUpSurveyModal
                open={showSignUpSurveyModal}
                onClose={() => {
                  setShowSignUpSurveyModal(false);
                }}
                signUpSurvey={event.signUpSurvey}
                signUpUserCallback={signupUserToEvent}
              />
            )}
            <EventSignUpsModal
              open={showEventSignUpsModal}
              onClose={() => {
                setShowEventSignupsModal(false);
              }}
              eventId={eventId}
            />
            <EventFeedbackResultsModal
              open={showEventFeedbackResultsModal}
              onClose={() => setShowEventFeedbackResultsModal(false)}
              eventId={eventId}
              eventName={event.title}
              eventOrganizerEmail={event.eventOrganizerEmail}
            />
            <Styled.ScrollableContent>
              <Styled.HeroContainer>
                <Styled.HeroBackground
                  style={{
                    backgroundImage: `url("${event.eventHeroS3Url || GatherDefaultImage
                      }")`,
                    filter: event.eventHeroS3Url
                      ? "blur(50px) brightness(.9)"
                      : "",
                  }}
                ></Styled.HeroBackground>
                <Styled.HeroImage
                  src={event.eventHeroS3Url || GatherDefaultImage}
                  alt={event.title}
                  loading="lazy"
                />
              </Styled.HeroContainer>
              <Stack
                direction={{ mobile: "column", desktop: "row" }}
                width="100%"
                spacing={{ mobile: 0, desktop: 6 }}
              >
                <Styled.DetailsContainer>
                  <Box>
                    <Typography
                      variant="modalSubtitle"
                      style={{ marginBottom: theme.spacing(1) }}
                    >
                      Details
                    </Typography>
                    <Styled.ModalTitleText variant="h1">
                      {event.title}
                    </Styled.ModalTitleText>
                    <Styled.ModalBodyMB2 variant="modalBody">
                      <Box
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(event.description),
                        }}
                      />
                    </Styled.ModalBodyMB2>
                    <Styled.EventActionButtonContainer
                      onClick={() => {
                        setShowQrCodeDetailsModal(true);
                      }}
                    >
                      <ShareIcon color="primary" />
                      <Typography
                        variant="body2"
                        style={{
                          marginLeft: theme.spacing(1),
                          marginRight: theme.spacing(1),
                        }}
                      >
                        Share Event
                      </Typography>
                    </Styled.EventActionButtonContainer>

                    <Stack direction={"row"} width="100%">
                      <Box style={{ width: "50%" }}>
                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Date
                        </Typography>
                        <Styled.ModalBodyMB2 variant="modalBody">
                          {getMonthAndDayFromMilliseconds(event.startDateTime)}
                          {getMonthAndDayFromMilliseconds(
                            event.startDateTime
                          ) !== "Today" && (
                            <Styled.SuperScript>
                              {nthNumber(event.startDateTime)}
                            </Styled.SuperScript>
                          )}
                        </Styled.ModalBodyMB2>

                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Dress Code
                        </Typography>
                        <Styled.ModalBodyMB2 variant="modalBody">
                          {event.dressCode || "N/A"}
                        </Styled.ModalBodyMB2>

                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Format
                        </Typography>
                        <Styled.ModalBodyMB2 variant="modalBody">
                          {event.format}
                        </Styled.ModalBodyMB2>
                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Event Tag(s)
                        </Typography>
                        <Styled.ModalBodyMB2 variant="modalBody">
                          {event.eventTags.join(', ')}
                        </Styled.ModalBodyMB2>
                      </Box>
                      <Box style={{ width: "50%" }}>
                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Time
                        </Typography>
                        <Styled.ModalBodyMB2 variant="modalBody">
                          {`${getHourFromMilliseconds(event.startDateTime)}${
                            getMinutesFromMilliseconds(event.startDateTime) !==
                            "00"
                              ? `:${getMinutesFromMilliseconds(
                                  event.startDateTime
                                )}`
                              : ""
                          }`}
                          <Styled.SuperScript>
                            {getAmOrPm(event.startDateTime)}
                          </Styled.SuperScript>
                          {" - "}
                          {`${getHourFromMilliseconds(event.endDateTime)}${
                            getMinutesFromMilliseconds(event.endDateTime) !==
                            "00"
                              ? `:${getMinutesFromMilliseconds(
                                  event.endDateTime
                                )}`
                              : ""
                          }`}
                          <Styled.SuperScript>
                            {getAmOrPm(event.endDateTime)}
                          </Styled.SuperScript>
                        </Styled.ModalBodyMB2>
                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Cost
                        </Typography>
                        <Styled.ModalBodyMB2 variant="modalBody">
                          {event.admissionCost
                            ? `$${event.admissionCost.toString()}`
                            : "N/A"}
                        </Styled.ModalBodyMB2>

                        {event.teamsLink !== "" && (
                          <>
                            <Typography
                              variant="subtitle2"
                              style={{ color: theme.colors.neutral.darkGray }}
                            >
                              Teams Meeting Link
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                transition: "opacity 0.3s ease",
                                "&:hover": {
                                  opacity: 0.7,
                                },
                              }}
                              onClick={() => {
                                window.open(event.teamsLink, "_blank");
                              }}
                            >
                              <MicrosoftIcon
                                color="primary"
                                sx={{ width: "14px", height: "14px" }}
                              />
                              <Typography
                                variant="modalBody"
                                style={{
                                  marginLeft: theme.spacing(1),
                                  marginRight: theme.spacing(1),
                                  color: theme.colors.primary.slalomBlue,
                                  textDecoration: "underline",
                                }}
                              >
                                Join In Teams
                              </Typography>
                            </Box>
                          </>
                        )}
                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Office
                        </Typography>
                        <Styled.ModalBodyMB2 variant="modalBody">
                          {event.office}
                        </Styled.ModalBodyMB2>
                      </Box>
                    </Stack>

                    <Accordion
                      loading={signupsLoading || signups.length === 0}
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Styled.SignUpsIcon />
                          {(signupsLoading || metricsLoading) ? (
                            <Skeleton variant="text" style={{ width: 200 }} />
                          ) : (
                            <Typography variant="body2">
                              {isPastEndTime && !isCancelled
                                ? `${metrics?.countAttended} Attendee${
                                    (metrics?.countAttended || 0) !== 1
                                      ? "s"
                                      : ""
                                  }`
                                : `${signups.length} Sign Up${
                                    signups.length !== 1 ? "s" : ""
                                  }`}
                            </Typography>
                          )}
                        </Box>
                      }
                      rightComponent={
                        user.email === event.eventOrganizerEmail &&
                        !signupsLoading &&
                        signups.length > 0 &&
                        event.signUpSurvey && (
                          <Styled.SurveyResultsButtonContainer
                            onClick={() =>
                              isPastEndTime && !isCancelled
                                ? setShowEventFeedbackResultsModal(true)
                                : setShowEventSignupsModal(true)
                            }
                          >
                            {isPastEndTime && !isCancelled ? (
                              <StarBorderIcon color="primary" />
                            ) : (
                              <CategoryOutlinedIcon color="primary" />
                            )}

                            <Typography
                              variant="body2"
                              style={{
                                marginLeft: theme.spacing(1),
                                marginRight: theme.spacing(1),
                              }}
                            >
                              {isPastEndTime && !isCancelled
                                ? "Event Feedback"
                                : "Survey Results"}
                            </Typography>
                          </Styled.SurveyResultsButtonContainer>
                        )
                      }
                    >
                      {(!isPastEndTime && !isCancelled
                        ? signups
                        : metrics?.attended || []
                      ).map((person) => {
                        const participantName =
                          person.participantName || person.name || person;
                        return (
                          <Contact
                            variant="modalBody"
                            size={24}
                            spacing={theme.spacing(1)}
                            name={participantName}
                            key={participantName}
                          />
                        );
                      })}
                    </Accordion>
                  </Box>
                </Styled.DetailsContainer>
                <Styled.LocationContainer>
                  <Typography
                    variant="modalSubtitle"
                    style={{
                      marginBottom: theme.spacing(2),
                    }}
                  >
                    Location
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    style={{
                      color: theme.colors.neutral.darkGray,
                    }}
                  >
                    Event Location
                  </Typography>
                  <Typography
                    variant="modalBody"
                    style={{
                      marginBottom: theme.spacing(2),
                    }}
                  >
                    {event.format === "Virtual" ? "Virtual" : event.location}
                  </Typography>
                  {generateEventLocationMap(event)}
                  {event.format !== "Virtual" && (
                    <Typography
                      variant="subtitle2"
                      style={{
                        color: theme.colors.neutral.darkGray,
                      }}
                    >
                      Transportation & Parking Details
                    </Typography>
                  )}
                  {event.format !== "Virtual" && (
                    <Styled.ModalBodyMB2 variant="modalBody">
                      {event.transportationParkingDetails || "N/A"}
                    </Styled.ModalBodyMB2>
                  )}
                  <Typography
                    variant="modalSubtitle"
                    style={{
                      marginBottom: theme.spacing(2),
                    }}
                  >
                    Primary Contact(s)
                  </Typography>
                  {event.primaryContacts.map((name: string) => {
                    return (
                      <Contact
                        variant="modalBody"
                        size={24}
                        spacing={theme.spacing(1)}
                        name={name}
                        key={name}
                      />
                    );
                  })}
                </Styled.LocationContainer>
              </Stack>
            </Styled.ScrollableContent>
            {event.status === "CANCELLED" ? (
              <Alert severity="warning" sx={{ marginTop: theme.spacing(1) }}>
                This event was cancelled by the event organizer.
              </Alert>
            ) : (
              <Styled.ButtonsContainer>
                {signupsLoading || signupUserLoading || unsignupUserLoading ? (
                  <Styled.LoaderContainer>
                    <CircularProgress />
                  </Styled.LoaderContainer>
                ) : (
                  <Stack
                    direction={{
                      mobile: "column",
                      tablet: "row",
                      desktop: "row",
                    }}
                    width="100%"
                    spacing={2}
                  >
                    {user.email === event.eventOrganizerEmail && (
                      <Button
                        startIcon={<EditIcon />}
                        variant="outlined"
                        onClick={() => {
                          window.scroll(0, 0);
                          navigate(`/edit/${eventId}`);
                        }}
                        disabled={Date.now() > event.endDateTime * 1000}
                      >
                        Edit Event
                      </Button>
                    )}
                    {unixDateDiff(event.startDateTime, dateToUnix(new Date())) /
                      60 >=
                      15 ? (
                      isSignedUp ? (
                        <>
                          <Button
                            startIcon={<RemoveCircleOutlineOutlinedIcon />}
                            variant="outlined"
                            onClick={() => {
                              unsignUserToEvent();
                            }}
                          >
                            Unsign Up
                          </Button>
                        </>
                      ) : (
                        <Button
                          startIcon={<AddCircleOutlineOutlinedIcon />}
                          variant="outlined"
                          onClick={() => {
                            if (event && event.signUpSurvey?.length > 0) {
                              setShowSignUpSurveyModal(true);
                            } else {
                              signupUserToEvent();
                            }
                          }}
                        >
                          Sign Up
                        </Button>
                      )
                    ) : (
                      generateLiveEventButton(event, user)
                    )}
                    {isSignedUp && (
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircleRoundedIcon
                          color="success"
                          style={{ marginRight: theme.spacing(1) }}
                        />
                        <Typography variant="body2">Signed Up</Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Styled.ButtonsContainer>
            )}
          </Stack>
        )}
      </BaseModal>
    );
  }
);

export default EventDetailsModal;
